import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { BranchCell } from "@/components/BranchFlag";
import { PageHeader, TableShell } from "@/components/EntityManager";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { BRANCHES, loadBranches } from "@/lib/branches";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, FileText, Pencil, Printer, Satellite, Radio, Minus, Maximize2, Minimize2, X, MessageCircle, Trash2, History, Recycle, RotateCcw, Search, FileSpreadsheet, Mail, Camera } from "lucide-react";
import { inr, fmtDate, today } from "@/lib/format";
import { printConsignment, printConsignments, printConsignmentRegister, printUnderInsured, printCoaCertificate, printCoaRegister, printClaimAgeing, coaCertificateHtml, LR_COPIES } from "@/lib/print";
import html2canvas from "html2canvas";
import { logPadIssue, PadLogButton } from "@/components/PadIssueLog";
import { NewBadge } from "@/components/ListPagination";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUSES = ["Pending", "In Transit", "Waiting for unloading", "Vehicle Break down", "Delivered", "Cancelled"];
const BASIS = ["PAID", "TBB", "TOPAY", "FOC"];
const MODES = ["AIR", "ROAD", "TRAIN", "EXPRESS"];
const GST_PAYABLE = ["CONSIGNOR", "CONSIGNEE", "TRANSPORT", "THIRD PARTY"];
const VEHICLE_TYPES = [
  { type: "Bollero pick up", cap: "1 MT" },
  { type: "14 Feet", cap: "3 MT" },
  { type: "17 Feet", cap: "5 MT" },
  { type: "19 Feet", cap: "7 MT" },
  { type: "20 Feet", cap: "6 MT" },
  { type: "22 Feet", cap: "6 MT" },
  { type: "10 Feet container", cap: "1 MT" },
  { type: "20 Feet container", cap: "6 MT" },
  { type: "24 Feet container", cap: "7 MT" },
  { type: "32 Feet SXL container", cap: "7 MT" },
  { type: "32 Feet MXL container", cap: "15 MT" },
  { type: "20 Feet Trailer", cap: "8 MT" },
  { type: "40 Feet Trailer", cap: "20 MT" },
  { type: "40 Feet Low Bed Trailer", cap: "20 MT" },
  { type: "40 Feet Semi Low Bed Trailer", cap: "20 MT" },
];

const EMPTY = {
  lr_no: "",
  lr_date: today(),
  origin: "",
  destination: "",
  office: "",
  delivery_at: "",
  consignor: "",
  consignee: "",
  consignor_address: "",
  consignor_gst: "",
  consignee_address: "",
  consignee_gst: "",
  insurance_company: "",
  insurance_policy_no: "",
  insurance_date: "",
  insurance_expiry: "",
  insurance_amount: 0,
  insurance_risk: "",
  container_type: "",
  customer_id: "",
  customer_name: "",
  basis: "",
  mode_of_operation: "",
  gst_payable_by: "",
  payable_by: "",
  third_party_name: "",
  third_party_gst: "",
  third_party_address: "",
  dc_no: "",
  dc_date: "",
  dc_value: 0,
  payment_mode: "",
  cheque_no: "",
  cheque_date: "",
  cheque_amount: 0,
  cheque_bank: "",
  vehicle_type: "",
  weight_capacity: "",
  weight_kgs: "",
  packages: 0,
  said_to_contain: "",
  vehicle_no: "",
  driver_mobile: "",
  owner_mobile: "",
  vendor_name: "",
  vendor_code: "",
  vendor_address: "",
  vendor_gst_no: "",
  office_name: "",
  vehicle_ownership: "Hired",
  freight_amount: 0,
  lorry_hire: 0,
  advance_amount: 0,
  loading_charge: 0,
  unloading_charge: 0,
  halting_charge: 0,
  odc_charge: 0,
  demurrage_charge: 0,
  fov_charge: 0,
  eway_bill: "",
  eway_valid: "",
  invoice_no: "",
  invoice_date: "",
  invoice_value: 0,
  extra_invoices: [],
  extra_dcs: [],
  extra_eways: [],
  goods: "",
  status: "Pending",
  dispatch_date: today(),
  delivery_date: "",
  remarks: "",
};

const shareCoaWhatsApp = async (r) => {
  try {
    toast.info("Preparing COA certificate image…");
    const el = document.createElement("div");
    el.style.cssText = "position:fixed;left:-20000px;top:0;background:#ffffff;width:780px;padding:12px;";
    el.innerHTML = coaCertificateHtml(r);
    document.body.appendChild(el);
    const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    document.body.removeChild(el);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
    const fname = `${String(r.coa_no || "coa").replace(/[^\w-]+/g, "-")}.png`;
    const file = new File([blob], fname, { type: "image/png" });
    const lr = String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "");
    const text = `COA Certificate ${r.coa_no || ""} — LR ${lr} (${r.origin || ""} → ${r.destination || ""})\nMaterial damage in transit — certificate attached for your insurance claim.\n— Nisha Transport Corporation`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: "NTC COA Certificate", text });
      return;
    }
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.click();
    URL.revokeObjectURL(url);
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    toast.success("COA image downloaded", { description: "Pick the party's chat in WhatsApp and attach the downloaded certificate image." });
  } catch (e) {
    if (e?.name === "AbortError") return;
    toast.error("Could not prepare the COA image");
  }
};

const COLS = [
  "Sl No.", "Branch", "LR No.", "LR Date", "From", "Destination", "Consignor Name", "Consignee Name", "Customer Code",
  "Basis", "Mode", "GST Payable By", "Payable By", "Invoice No.", "Invoice Date", "Invoice Value", "DC No.", "DC Date", "DC Value", "Vehicle Type", "Weight", "Packages", "Description (Said to Contain)", "Freight Amount", "Lorry Hire", "Advance Amount",
  "Balance Amount", "Loading Charge", "Unloading Charge", "Halting charge", "ODC Charge", "Demurrage Charge", "FOV Charge", "E Way Bill No",
  "E Way Bill Valid", "Vehicle No.", "Vehicle Type", "Driver Mobile No", "Vendor/Broker Name", "Vendor/Broker Code", "Delivery Date", "Status", "COA",
];

function TextField({ k, label, placeholder, type = "text", form, onText, listId }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Input type={type} list={listId} data-testid={`field-${k}`} value={form[k] ?? ""} placeholder={placeholder} onChange={(e) => onText(k, e.target.value)} />
    </div>
  );
}

function NumberField({ k, label, form, onNum, disabled, hint }) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Input type="number" data-testid={`field-${k}`} value={form[k] ?? 0} onChange={(e) => onNum(k, e.target.value)} disabled={disabled} className={disabled ? "bg-slate-50" : ""} title={disabled ? hint : undefined} />
      {disabled && hint ? <span className="text-[10px] font-semibold text-amber-600">{hint}</span> : null}
    </div>
  );
}

function BlankLrDialog({ open, onClose, onPrint, existingRows }) {
  const [startNo, setStartNo] = useState("");
  const [count, setCount] = useState(1);
  const [clashes, setClashes] = useState([]);
  const [warned, setWarned] = useState(false);
  useEffect(() => { if (open) { setStartNo(""); setCount(1); setClashes([]); setWarned(false); } }, [open]);
  const buildList = () => {
    const n = Math.min(Math.max(Number(count) || 1, 1), 50);
    const m = String(startNo).trim().match(/^(.*?)(\d+)$/);
    return Array.from({ length: n }, (_, i) => {
      if (!startNo.trim()) return { blank: true };
      if (!m || n === 1) return { blank: true, lr_no: startNo.trim() };
      return { blank: true, lr_no: m[1] + String(Number(m[2]) + i).padStart(m[2].length, "0") };
    });
  };
  const go = () => {
    const list = buildList();
    const norm = (v) => String(v || "").replace(/^CN[-\s]?/i, "").trim().toLowerCase();
    const used = new Set((existingRows || []).map((r) => norm(r.lr_no || r.consignment_no)).filter(Boolean));
    const bad = list.map((l) => l.lr_no).filter((no) => no && used.has(norm(no)));
    if (bad.length && !warned) {
      setClashes(bad);
      setWarned(true);
      return;
    }
    onPrint(list);
    onClose();
  };
  const onChange = (fn) => (e) => { fn(e.target.value); setWarned(false); setClashes([]); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[380px]" data-testid="blank-lr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Blank LR Print</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Starting LR No. (manual entry)</Label>
            <Input value={startNo} onChange={onChange(setStartNo)} placeholder="e.g. 4601 (leave blank for no number)" data-testid="blank-lr-start-no" />
          </div>
          <div className="grid gap-1.5">
            <Label>How many blank LRs</Label>
            <Input type="number" min="1" max="50" value={count} onChange={onChange(setCount)} data-testid="blank-lr-count" />
            <p className="text-xs text-muted-foreground">Serial numbers auto-increment from the starting LR No. so hand-filled notes stay traceable.</p>
          </div>
          {clashes.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="blank-lr-clash-warning">
              ⚠ LR No. {clashes.join(", ")} already exist{clashes.length === 1 ? "s" : ""} in the system. Click "Print Anyway" to continue or change the number.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant={clashes.length ? "destructive" : "default"} onClick={go} data-testid="blank-lr-continue-btn">
            <Printer className="mr-1.5 h-4 w-4" /> {clashes.length ? "Print Anyway" : "Choose Copies"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LrCopyChoiceDialog({ target, onClose }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [picked, setPicked] = useState(LR_COPIES);
  useEffect(() => { if (target) setPicked(LR_COPIES); }, [target]);
  const toggle = (cp) => setPicked((p) => (p.includes(cp) ? p.filter((x) => x !== cp) : [...p, cp]));
  const doPrint = async () => {
    const copies = LR_COPIES.filter((c) => picked.includes(c));
    if (Array.isArray(target)) {
      printConsignments(target, copies);
      if (target[0]?.blank) logPadIssue("LR", target, "lr_no");
    } else {
      let dup = false;
      if (target?.id) {
        try {
          const res = await api.post(`/shipments/${target.id}/mark-printed`);
          dup = !isAdmin && (res.data.print_count || 1) > 1;
        } catch {
          dup = false;
        }
      }
      printConsignment(target, copies, { duplicate: dup });
    }
    onClose();
  };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[360px]" data-testid="lr-copy-choice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Which copies to print?</DialogTitle>
        </DialogHeader>
        <div className="grid gap-1.5">
          {LR_COPIES.map((cp) => (
            <label key={cp} className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm font-medium ${picked.includes(cp) ? "border-primary/40 bg-primary/5" : "border-border"}`}>
              <input type="checkbox" className="h-4 w-4 accent-blue-600" checked={picked.includes(cp)} onChange={() => toggle(cp)} data-testid={`lr-copy-check-${cp.split(" ")[0].toLowerCase()}`} />
              {cp.charAt(0) + cp.slice(1).toLowerCase()}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={doPrint} disabled={picked.length === 0} data-testid="lr-copy-choice-print-btn">
            <Printer className="mr-1.5 h-4 w-4" /> Print {picked.length} cop{picked.length === 1 ? "y" : "ies"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsignmentEntry() {
  const { user } = useAuth();
  const token = localStorage.getItem("ntc_token");
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState(null);
  const coaAutoRef = useRef(false);
  useEffect(() => {
    if (coaAutoRef.current || rows === null) return;
    if (new URLSearchParams(window.location.search).get("coa") === "1") {
      coaAutoRef.current = true;
      window.history.replaceState({}, "", window.location.pathname);
      const issued = (rows || []).filter((r) => r.coa_no);
      if (issued.length) printCoaRegister(issued);
      else toast.info("No COA certificates issued yet.");
    }
  }, [rows]);
  const [coaRow, setCoaRow] = useState(null);
  const [coaDesc, setCoaDesc] = useState("");
  const [coaValue, setCoaValue] = useState("");
  const [coaCategory, setCoaCategory] = useState("");
  const [coaIssueValue, setCoaIssueValue] = useState("");
  const [coaDate, setCoaDate] = useState("");
  const [coaSaving, setCoaSaving] = useState(false);
  const withCoaPhotos = (r) => ({ ...r, _photoUrls: (r.coa_photos || []).map((p) => `${API}/files/${p}?auth=${token}`) });
  const [photoRow, setPhotoRow] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [emailingCoaId, setEmailingCoaId] = useState("");
  const emailCoa = async (r) => {
    setEmailingCoaId(r.id);
    try {
      let to = "";
      try {
        const cfg = await api.get("/settings/insurer-email");
        to = cfg.data?.email || "";
      } catch {}
      if (!to) {
        to = window.prompt("Insurance agent email (saved for next time):") || "";
        if (!to.trim()) return;
        await api.put("/settings/insurer-email", { email: to.trim() });
      }
      const res = await api.post(`/shipments/${r.id}/coa/email`, {});
      if (res.data?.ok) toast.success(`COA ${r.coa_no} emailed to ${res.data.to}`);
      else toast.error(res.data?.error || "Email could not be delivered");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEmailingCoaId("");
    }
  };
  const uploadCoaPhotos = async (files) => {
    if (!photoRow || !files?.length) return;
    setPhotoBusy(true);
    try {
      let updated = photoRow;
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post(`/consignments/${photoRow.id}/coa-photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        updated = { ...updated, coa_photos: [...(updated.coa_photos || []), res.data.path] };
      }
      setPhotoRow(updated);
      setRows((rs) => (rs || []).map((x) => (x.id === updated.id ? updated : x)));
      toast.success("Damage photo(s) attached");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPhotoBusy(false);
    }
  };
  const deleteCoaPhoto = async (p) => {
    try {
      await api.delete(`/consignments/${photoRow.id}/coa-photos`, { params: { path: p } });
      const updated = { ...photoRow, coa_photos: (photoRow.coa_photos || []).filter((x) => x !== p) };
      setPhotoRow(updated);
      setRows((rs) => (rs || []).map((x) => (x.id === updated.id ? updated : x)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const [claimRow, setClaimRow] = useState(null);
  const [claimStatus, setClaimStatus] = useState("Filed");
  const [claimAmount, setClaimAmount] = useState("");
  const [claimSaving, setClaimSaving] = useState(false);
  const saveClaim = async () => {
    setClaimSaving(true);
    try {
      await api.post(`/shipments/${claimRow.id}/coa/claim`, { claim_status: claimStatus, claim_amount: Number(claimAmount) || 0 });
      const updated = { ...claimRow, coa_claim_status: claimStatus, coa_claim_amount: Number(claimAmount) || 0 };
      setRows((rs) => (rs || []).map((x) => (x.id === updated.id ? updated : x)));
      setClaimRow(null);
      toast.success(`Claim marked ${claimStatus}${claimStatus === "Settled" && Number(claimAmount) ? ` — ₹${Number(claimAmount).toLocaleString("en-IN")} received` : ""}`);
      if (claimStatus === "Settled") {
        const cust = customers.find((c) => c.id === claimRow.customer_id);
        const phone = String(cust?.phone || "").replace(/\D/g, "");
        const lr = String(claimRow.lr_no || claimRow.consignment_no || "").replace(/^CN[-\s]?/i, "");
        const msg = [
          `Dear ${cust?.name || claimRow.customer_name || "Customer"},`,
          "",
          `Good news — the insurance claim for your consignment LR ${lr} (${claimRow.origin || ""} → ${claimRow.destination || ""}) has been *SETTLED*.`,
          `COA Certificate: ${claimRow.coa_no}`,
          Number(claimAmount) ? `Amount received: ₹${Number(claimAmount).toLocaleString("en-IN")}` : "",
          "",
          "Thank you for your patience.",
          "— Nisha Transport Corporation",
        ].filter((l) => l !== "").join("\n");
        if (phone) {
          window.open(`https://wa.me/${phone.length === 10 ? "91" + phone : phone}?text=${encodeURIComponent(msg)}`, "_blank");
        } else {
          window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
          toast.info("Customer has no phone saved — pick the chat in WhatsApp.");
        }
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClaimSaving(false);
    }
  };
  const [noteText, setNoteText] = useState("");
  const [crRow, setCrRow] = useState(null);
  const [crField, setCrField] = useState("freight_amount");
  const [crValue, setCrValue] = useState("");
  const [crReason, setCrReason] = useState("");
  const [crBusy, setCrBusy] = useState(false);
  const [myCrs, setMyCrs] = useState({});
  useEffect(() => {
    if (isAdmin) return;
    api.get("/change-requests").then((res) => {
      const m = {};
      const cutoff = new Date(Date.now() - 7 * 86400000).toISOString();
      (res.data || []).forEach((cr) => {
        if (cr.status !== "Pending" && cr.decided_at && cr.decided_at < cutoff) return;
        if (!m[cr.shipment_id]) m[cr.shipment_id] = cr;
      });
      setMyCrs(m);
    }).catch(() => {});
  }, [isAdmin]);
  const submitChangeRequest = async () => {
    setCrBusy(true);
    try {
      const res = await api.post(`/shipments/${crRow.id}/change-request`, { field: crField, new_value: String(crValue).trim(), reason: crReason.trim() });
      setMyCrs((m) => ({ ...m, [crRow.id]: res.data }));
      toast.success("Correction request sent to Admin for approval");
      setCrRow(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCrBusy(false);
    }
  };
  const [noteBusy, setNoteBusy] = useState(false);
  const patchClaimRow = (updated) => {
    setClaimRow(updated);
    setRows((rs) => (rs || []).map((x) => (x.id === updated.id ? updated : x)));
  };
  const addClaimNote = async () => {
    if (!noteText.trim()) return;
    setNoteBusy(true);
    try {
      const res = await api.post(`/shipments/${claimRow.id}/coa/notes`, { text: noteText.trim() });
      patchClaimRow({ ...claimRow, coa_notes: [...(claimRow.coa_notes || []), res.data] });
      setNoteText("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setNoteBusy(false);
    }
  };
  const deleteClaimNote = async (at) => {
    try {
      await api.delete(`/shipments/${claimRow.id}/coa/notes`, { params: { at } });
      patchClaimRow({ ...claimRow, coa_notes: (claimRow.coa_notes || []).filter((n) => n.at !== at) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const shareAgeingWhatsApp = async () => {
    const openClaims = (rows || []).filter((r) => r.coa_no && (r.coa_claim_status || "Not Filed") !== "Settled");
    if (!openClaims.length) {
      toast.info("No open claims — all COA claims are settled.");
      return;
    }
    let phone = "";
    try {
      phone = (await api.get("/settings/insurer-phone")).data?.phone || "";
    } catch {}
    if (!phone) {
      phone = (window.prompt("Insurance agent WhatsApp number (saved for next time):") || "").replace(/\D/g, "");
      if (!phone) return;
      try {
        await api.put("/settings/insurer-phone", { phone });
      } catch {}
    }
    const days = (d) => (d ? Math.floor((Date.now() - new Date(d)) / 86400000) : 0);
    const sorted = [...openClaims].sort((a, b) => days(b.coa_date) - days(a.coa_date));
    const total = openClaims.reduce((s, r) => s + (Number(r.coa_damage_value) || 0), 0);
    const msg = [
      "*NTC — Open Insurance Claims (Ageing Summary)*",
      `As on ${new Date().toLocaleDateString("en-IN")} — ${openClaims.length} open claim(s)`,
      "",
      ...sorted.map((r, i) => `${i + 1}. ${r.coa_no} — LR ${String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "")} — ${r.consignor || r.customer_name || "—"} — ₹${(Number(r.coa_damage_value) || 0).toLocaleString("en-IN")} — ${r.coa_claim_status || "Not Filed"} — ${days(r.coa_date)} days`),
      "",
      `Total damage value under open claims: ₹${total.toLocaleString("en-IN")}`,
      "Request your review and status update ahead of our meeting.",
      "— Nisha Transport Corporation",
    ].join("\n");
    window.open(`https://wa.me/${phone.length === 10 ? "91" + phone : phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const issueCoa = async (mode = "print") => {
    if (!coaDesc.trim()) {
      toast.error("Describe the damage first");
      return;
    }
    setCoaSaving(true);
    try {
      const res = await api.post(`/shipments/${coaRow.id}/coa`, { damage_desc: coaDesc.trim(), damage_value: Number(coaValue) || 0, coa_date: coaDate, damage_category: coaCategory.trim(), issue_value: Number(coaIssueValue) || 0 });
      const updated = { ...coaRow, coa_no: res.data.coa_no, coa_date: res.data.coa_date, coa_damage_desc: coaDesc.trim(), coa_damage_value: Number(coaValue) || 0, coa_damage_category: coaCategory.trim(), coa_issue_value: Number(coaIssueValue) || 0 };
      setRows((rs) => (rs || []).map((x) => (x.id === coaRow.id ? updated : x)));
      setCoaRow(null);
      toast.success(`COA certificate ${res.data.coa_no} issued`);
      if (mode === "wa") await shareCoaWhatsApp(updated);
      else printCoaCertificate(updated);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCoaSaving(false);
    }
  };
  const [branchList, setBranchList] = useState([...BRANCHES]);
  const [branchFilter, setBranchFilter] = useState("All");
  const [noBranchOnly, setNoBranchOnly] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;
  const branchMatch = (r) => (noBranchOnly ? !(r.office || r.office_name) : branchFilter === "All" || (r.office || r.office_name || "") === branchFilter);
  const [q, setQ] = useState("");
  const rowCode = (r) => customers.find((c) => c.id === r.customer_id)?.customer_code || r.customer_code || "";
  const viewMatch = (r) => {
    if (!branchMatch(r)) return false;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const hay = [String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""), r.consignor, r.consignee, rowCode(r), r.origin, r.destination, r.vehicle_no, r.invoice_no, r.dc_no, ...(r.extra_invoices || []).map((x) => x.no), ...(r.extra_dcs || []).map((x) => x.no)];
    return hay.some((x) => String(x || "").toLowerCase().includes(needle));
  };
  const [lrQuery, setLrQuery] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const jumpToLr = () => {
    const q = lrQuery.trim().toLowerCase().replace(/^cn[-\s]?/, "");
    if (!q) return;
    const list = (rows || []).filter(branchMatch);
    const key = (r) => String(r.lr_no || r.consignment_no || "").toLowerCase().replace(/^cn[-\s]?/, "");
    let idx = list.findIndex((r) => key(r) === q);
    if (idx === -1) idx = list.findIndex((r) => key(r).includes(q));
    if (idx === -1) {
      toast.error(`LR "${lrQuery.trim()}" not found${branchFilter !== "All" ? ` in ${branchFilter}` : ""}`);
      return;
    }
    const target = Math.floor(idx / PAGE_SIZE) + 1;
    setPage(target);
    setHighlightId(list[idx].id);
    toast.success(`LR found on page ${target}`);
    setTimeout(() => setHighlightId(null), 5000);
  };
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [freightRates, setFreightRates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rcOpen, setRcOpen] = useState(false);
  const [rc, setRc] = useState({ freight: "", basis: "", hire: "", reason: "" });
  const [myRequests, setMyRequests] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [regFrom, setRegFrom] = useState("");
  const [regTo, setRegTo] = useState("");
  const [logOpen, setLogOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [binOpen, setBinOpen] = useState(false);
  const [binItems, setBinItems] = useState(null);

  const openRecycleBin = async () => {
    setBinOpen(true);
    try {
      const res = await api.get("/recycle-bin");
      setBinItems(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const restoreItem = async (item) => {
    try {
      await api.post(`/recycle-bin/${item.id}/restore`);
      setBinItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success(`${item.ref} restored`);
      if (item.entity === "Consignment") loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const purgeItem = async (item) => {
    if (!window.confirm(`Permanently delete ${item.ref}? This cannot be undone.`)) return;
    try {
      await api.delete(`/recycle-bin/${item.id}`);
      setBinItems((prev) => prev.filter((x) => x.id !== item.id));
      toast.success(`${item.ref} permanently deleted`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const emptyBin = async () => {
    if (!window.confirm("Permanently delete ALL items in the recycle bin? This cannot be undone.")) return;
    try {
      const res = await api.post("/recycle-bin/empty");
      setBinItems([]);
      toast.success(`${res.data.purged} item(s) permanently deleted`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openDeleteLog = async () => {
    setLogOpen(true);
    try {
      const res = await api.get("/delete-logs");
      setLogs(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected consignment(s)? This cannot be undone.`)) return;
    try {
      const res = await api.post("/shipments/bulk-delete", { ids });
      setRows((prev) => prev.filter((x) => !selected.has(x.id)));
      setSelected(new Set());
      toast.success(`${res.data.deleted} consignment(s) deleted`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const [mode, setMode] = useState("single");
  const [custCode, setCustCode] = useState("");
  const [vendors, setVendors] = useState([]);
  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    if (!isAdmin) api.get("/rate-change-requests").then((r) => setMyRequests(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [printTarget, setPrintTarget] = useState(null);
  const [blankLrOpen, setBlankLrOpen] = useState(false);
  const [multiRows, setMultiRows] = useState([]);
  const [dlgView, setDlgView] = useState("normal");

  const blankRow = () => ({ lr_no: "", consignor: "", consignee: "", destination: "", office_name: "", packages: 0, said_to_contain: "", freight_amount: 0 });
  const addMultiRow = () => setMultiRows((rs) => [...rs, blankRow()]);
  const updateMultiRow = (i, k, v) => setMultiRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const removeMultiRow = (i) => setMultiRows((rs) => rs.filter((_, idx) => idx !== i));
  const pasteMultiRows = async () => {
    let text = "";
    try { text = await navigator.clipboard.readText(); } catch (e) { /* ignore */ }
    if (!text) text = window.prompt("Paste rows from Excel — columns: LR No., Consignor, Consignee, Destination, Branch, Packages, Said to Contain, Freight") || "";
    if (!text.trim()) return;
    const newRows = text.trim().split(/\r?\n/).map((line) => {
      const c = line.split("\t");
      return {
        lr_no: (c[0] || "").trim(),
        consignor: (c[1] || "").trim(),
        consignee: (c[2] || "").trim(),
        destination: (c[3] || "").trim(),
        office_name: (c[4] || "").trim(),
        packages: Number(String(c[5] || "").replace(/[^0-9.]/g, "")) || 0,
        said_to_contain: (c[6] || "").trim(),
        freight_amount: Number(String(c[7] || "").replace(/[^0-9.-]/g, "")) || 0,
      };
    });
    setMultiRows((rs) => [...rs, ...newRows]);
    toast.success(`${newRows.length} row(s) pasted`);
  };

  const printRegister = () => {
    const inRange = (r) => {
      const d = (r.lr_date || r.dispatch_date || "").slice(0, 10);
      if (regFrom && (!d || d < regFrom)) return false;
      if (regTo && (!d || d > regTo)) return false;
      return true;
    };
    const list = (rows || []).filter(inRange).filter(branchMatch);
    if (list.length === 0) {
      toast.info("No consignments in the selected date range");
      return;
    }
    printConsignmentRegister(list, regFrom, regTo, branchFilter !== "All" ? `CONSIGNMENT REGISTER — ${branchFilter.toUpperCase()} BRANCH` : "CONSIGNMENT REGISTER");
  };

  const toggleRow = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  const allSelected = rows && rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected((prev) => {
      if (!rows) return prev;
      const n = new Set(prev);
      if (allSelected) rows.forEach((r) => n.delete(r.id));
      else rows.forEach((r) => n.add(r.id));
      return n;
    });
  const printSelected = () => {
    const chosen = (rows || []).filter((r) => selected.has(r.id));
    if (chosen.length === 0) {
      toast.info("Select at least one consignment to print");
      return;
    }
    printConsignments(chosen);
  };
  const selectedCount = rows ? rows.filter((r) => selected.has(r.id)).length : 0;

  const loadAll = async () => {
    try {
      const [s, c, v, fr] = await Promise.all([api.get("/shipments"), api.get("/customers"), api.get("/vehicles"), api.get("/freight-master").catch(() => ({ data: [] }))]);
      setRows(s.data);
      setCustomers(c.data);
      setVehicles(v.data);
      setFreightRates(fr.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const findVehicle = (vno) => {
    const norm = (x) => String(x || "").replace(/\s+/g, "").toUpperCase();
    const key = norm(vno);
    if (!key) return null;
    return vehicles.find((v) => norm(v.registration_no) === key) || null;
  };
  useEffect(() => {
    loadAll();
    loadBranches().then(() => setBranchList([...BRANCHES]));
  }, []);

  const normTxt = (s) => String(s || "").trim().toLowerCase();
  const rateActiveOn = (r, ref) => (!r.valid_from || r.valid_from <= ref) && (!r.valid_till || r.valid_till >= ref);
  const matchingRates = useMemo(() => {
    if (!form.customer_name || form.customer_name === "THIRD PARTY" || !normTxt(form.origin) || !normTxt(form.destination)) return null;
    return freightRates.filter(
      (r) => normTxt(r.customer_name) === normTxt(form.customer_name) && normTxt(r.origin) === normTxt(form.origin) && normTxt(r.destination) === normTxt(form.destination)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_name, form.origin, form.destination, freightRates]);

  const freightRateHint = useMemo(() => {
    if (matchingRates === null) return null;
    if (matchingRates.length === 0)
      return { tone: "missing", text: `No Freight Master rate saved for ${form.customer_name} · ${String(form.origin).trim().toUpperCase()} → ${String(form.destination).trim().toUpperCase()}. You can add one in Freight Master.` };
    const ref = form.lr_date || today();
    if (!matchingRates.some((r) => rateActiveOn(r, ref)))
      return { tone: "expired", text: `Freight Master rate for this route is not valid on ${fmtDate(ref)} — expired rates are not auto-applied.` };
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchingRates, form.lr_date]);

  useEffect(() => {
    if (!open) return;
    if (Number(form.freight_amount) > 0 || !matchingRates) return;
    const ref = form.lr_date || today();
    const rate = matchingRates.find((r) => rateActiveOn(r, ref) && Number(r.freight_amount) > 0);
    if (rate) {
      setForm((f) => ({
        ...f,
        freight_amount: Number(rate.freight_amount),
        ...(Number(rate.loading_charge) > 0 && !(Number(f.loading_charge) > 0) ? { loading_charge: Number(rate.loading_charge) } : {}),
        ...(Number(rate.unloading_charge) > 0 && !(Number(f.unloading_charge) > 0) ? { unloading_charge: Number(rate.unloading_charge) } : {}),
      }));
      const extras = [
        Number(rate.loading_charge) > 0 ? `Loading ${inr(rate.loading_charge)}` : "",
        Number(rate.unloading_charge) > 0 ? `Unloading ${inr(rate.unloading_charge)}` : "",
      ].filter(Boolean).join(" + ");
      toast.success(`Freight ${inr(rate.freight_amount)}${extras ? ` + ${extras}` : ""} auto-filled from Freight Master (${rate.unit || "Per Trip"})`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, matchingRates, form.lr_date]);

  const lowRateWarning = useMemo(() => {
    const typed = Number(form.freight_amount) || 0;
    if (!typed || !matchingRates || !matchingRates.length) return null;
    const ref = form.lr_date || today();
    const rate = matchingRates.find((r) => rateActiveOn(r, ref) && Number(r.freight_amount) > 0);
    if (!rate || typed >= Number(rate.freight_amount)) return null;
    const pct = Math.round((1 - typed / Number(rate.freight_amount)) * 100);
    return `Low rate warning: ${inr(typed)} is ${pct}% below the agreed rate card ${inr(rate.freight_amount)}${rate.unit ? ` (${rate.unit})` : ""} for this customer & route — check for underpricing.`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.freight_amount, matchingRates, form.lr_date]);

  const balance = useMemo(
    () => (Number(form.lorry_hire) || 0) - (Number(form.advance_amount) || 0),
    [form.lorry_hire, form.advance_amount]
  );

  const partyLookup = useMemo(() => {
    const consignors = {};
    const consignees = {};
    (rows || []).forEach((r) => {
      const cn = (r.consignor || "").trim().toLowerCase();
      if (cn && (r.consignor_address || r.consignor_gst) && !consignors[cn]) consignors[cn] = { address: r.consignor_address || "", gst: r.consignor_gst || "" };
      const ce = (r.consignee || "").trim().toLowerCase();
      if (ce && (r.consignee_address || r.consignee_gst) && !consignees[ce]) consignees[ce] = { address: r.consignee_address || "", gst: r.consignee_gst || "" };
    });
    return { consignor: consignors, consignee: consignees };
  }, [rows]);

  const set = (key, val) =>
    setForm((f) => {
      const next = { ...f, [key]: val };
      if (key === "consignor" || key === "consignee") {
        const hit = partyLookup[key][String(val).trim().toLowerCase()];
        if (hit) {
          if (!next[`${key}_address`]) next[`${key}_address`] = hit.address;
          if (!next[`${key}_gst`]) next[`${key}_gst`] = hit.gst;
        }
      }
      if (key === "vendor_name" || key === "vendor_code") {
        const v = String(val).trim().toLowerCase();
        const hit = vendors.find((x) =>
          key === "vendor_name"
            ? String(x.name || "").trim().toLowerCase() === v
            : String(x.vendor_code || "").trim().toLowerCase() === v && v
        );
        if (hit) {
          if (key === "vendor_code" && !next.vendor_name) next.vendor_name = hit.name || "";
          if (key === "vendor_name" && !next.vendor_code) next.vendor_code = hit.vendor_code || "";
          if (!next.vendor_address) next.vendor_address = hit.address || "";
          if (!next.vendor_gst_no) next.vendor_gst_no = hit.gstin || "";
        }
      }
      return next;
    });
  const setNum = (key, val) => setForm((f) => ({ ...f, [key]: Number(val) }));
  const underInsured = () =>
    (rows || [])
      .map((r) => {
        const invTotal = (Number(r.invoice_value) || 0) + (r.extra_invoices || []).reduce((s, x) => s + (Number(x.value) || 0), 0);
        const insured = Number(r.insurance_amount) || 0;
        return {
          date: r.lr_date || r.dispatch_date || "",
          lr: r.lr_no || r.consignment_no || "",
          party: r.consignor || r.customer_name || "",
          route: `${r.origin || ""} → ${r.destination || ""}`,
          invTotal,
          insured,
          short: invTotal - insured,
        };
      })
      .filter((r) => r.invTotal > 0 && r.short > 0);
  const addExtra = (key) => setForm((f) => ({ ...f, [key]: [...(f[key] || []), { no: "", date: "", value: 0 }] }));
  const rmExtra = (key, i) => setForm((f) => ({ ...f, [key]: (f[key] || []).filter((_, x) => x !== i) }));
  const setExtra = (key, i, field, val) =>
    setForm((f) => {
      const arr = [...(f[key] || [])];
      arr[i] = { ...arr[i], [field]: val };
      return { ...f, [key]: arr };
    });

  const openCreate = () => {
    setEditing(null);
    setMode("single");
    setMultiRows([blankRow()]);
    setForm({ ...EMPTY, lr_date: today(), dispatch_date: today() });
    setCustCode("");
    setDlgView("normal");
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setMode("single");
    setForm({ ...EMPTY, ...row, vehicle_ownership: row.vehicle_ownership || (row.office_name ? "Company" : "Hired") });
    setCustCode(customers.find((c) => c.id === row.customer_id)?.customer_code || "");
    setDlgView("normal");
    setOpen(true);
  };

  const pickVehicleType = (t) => {
    const v = VEHICLE_TYPES.find((x) => x.type === t);
    setForm((f) => ({ ...f, vehicle_type: t, weight_capacity: v ? v.cap : f.weight_capacity }));
  };

  const save = async () => {
    if (mode === "multi") {
      const valid = multiRows.filter((r) => r.lr_no || r.destination || r.consignor);
      if (!valid.length) {
        toast.error("Add at least one consignment row (LR No. / Destination / Consignor)");
        return;
      }
      setSaving(true);
      try {
        const sharedFields = {
          lr_date: form.lr_date,
          dispatch_date: form.lr_date,
          origin: form.origin,
          vehicle_no: form.vehicle_no,
          vehicle_type: form.vehicle_type,
          weight_capacity: form.weight_capacity,
          basis: form.basis,
          mode_of_operation: form.mode_of_operation,
          gst_payable_by: form.gst_payable_by,
          payable_by: form.payable_by,
          status: "Pending",
        };
        for (const r of valid) {
          await api.post("/shipments", { ...EMPTY, ...sharedFields, ...r });
        }
        toast.success(`${valid.length} consignment(s) created`);
        setOpen(false);
        loadAll();
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!form.origin || !form.destination) {
      toast.error("From and Destination are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, balance_amount: balance };
      if (editing) {
        await api.put(`/shipments/${editing.id}`, payload);
        toast.success("Consignment updated");
      } else {
        const res = await api.post("/shipments", payload);
        toast.success(`Consignment ${res.data.lr_no || res.data.consignment_no} created`, {
          description: "Do you want to print the consignment note now?",
          duration: 9000,
          action: { label: "Print Note", onClick: () => setPrintTarget(res.data) },
        });
      }
      setOpen(false);
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {!isAdmin && myRequests.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2" data-testid="my-rate-requests-strip">
          <span className="text-xs font-bold text-violet-900">My change requests:</span>
          {myRequests.slice(0, 5).map((r) => {
            const cls = r.status === "Approved" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : r.status === "Dismissed" ? "border-red-200 bg-red-50 text-red-600" : "border-amber-300 bg-amber-50 text-amber-700";
            return (
              <span key={r.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`} data-testid="my-rate-request-chip">
                LR {String(r.lr_no || "").replace(/^CN[-\s]?/i, "")} · {r.status}{r.status !== "Pending" && r.decided_by ? ` by ${r.decided_by}` : ""}
              </span>
            );
          })}
        </div>
      )}
      <PageHeader
        title="Consignment Entry"
        subtitle="Create a new consignment note (LR/GC) in the standard register format."
        action={
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={openRecycleBin} data-testid="recycle-bin-btn">
                <Recycle className="mr-2 h-4 w-4" /> Recycle Bin
              </Button>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={openDeleteLog} data-testid="delete-log-btn">
                <History className="mr-2 h-4 w-4" /> Delete Log
              </Button>
            )}
            {isAdmin && selectedCount > 0 && (
              <Button variant="destructive" onClick={bulkDelete} data-testid="bulk-delete-consignments-btn">
                <Trash2 className="mr-2 h-4 w-4" /> Delete Selected ({selectedCount})
              </Button>
            )}
            <Button variant="secondary" onClick={printSelected} disabled={selectedCount === 0} data-testid="print-selected-consignments-btn">
              <Printer className="mr-2 h-4 w-4" /> Print Selected{selectedCount ? ` (${selectedCount})` : ""}
            </Button>
            <Button data-testid="add-consignment-btn" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> New Consignment
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-border bg-slate-50/60 p-3">
        <div className="grid gap-1.5">
          <Label className="text-xs">Branch</Label>
          <Select value={branchFilter} onValueChange={(v) => { setBranchFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[170px] bg-white" data-testid="consignment-branch-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All Branches</SelectItem>
              {branchList.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className={`h-10 text-xs font-bold ${noBranchOnly ? "bg-amber-500 text-white hover:bg-amber-600" : "border border-amber-300 bg-white text-amber-700 hover:bg-amber-50"}`}
          onClick={() => { setNoBranchOnly((v) => !v); setPage(1); }}
          data-testid="consignment-no-branch-toggle"
        >
          {noBranchOnly ? "✓ " : ""}No Branch only ({(rows || []).filter((r) => !(r.office || r.office_name)).length})
        </Button>
        <div className="grid gap-1.5">
          <Label className="text-xs">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              className="w-[200px] bg-white pl-8 pr-7"
              placeholder="Customer / code / LR"
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              data-testid="consignment-search"
            />
            {q && (
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => { setQ(""); setPage(1); }} data-testid="consignment-search-clear">
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Jump to LR No.</Label>
          <div className="flex gap-1.5">
            <Input
              className="w-[140px] bg-white"
              placeholder="LR No."
              value={lrQuery}
              onChange={(e) => setLrQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && jumpToLr()}
              data-testid="lr-jump-input"
            />
            <Button variant="outline" size="icon" onClick={jumpToLr} title="Jump to LR" data-testid="lr-jump-btn">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Register From</Label>
          <Input type="date" className="w-[170px] bg-white" value={regFrom} onChange={(e) => setRegFrom(e.target.value)} data-testid="register-from" />
        </div>
        <div className="grid gap-1.5">
          <Label className="text-xs">Register To</Label>
          <Input type="date" className="w-[170px] bg-white" value={regTo} onChange={(e) => setRegTo(e.target.value)} data-testid="register-to" />
        </div>
        <Button variant="outline" onClick={printRegister} data-testid="print-register-btn">
          <Printer className="mr-2 h-4 w-4" /> Print Register
        </Button>
        <Button
          variant="outline"
          className="border-red-300 text-red-600 hover:bg-red-50"
          onClick={() => {
            const under = underInsured();
            if (!under.length) {
              toast.info("No under-insured consignments — every load is fully covered.");
              return;
            }
            printUnderInsured(under);
          }}
          data-testid="insured-report-btn"
        >
          <Printer className="mr-2 h-4 w-4" /> Insured Report
        </Button>
        {isAdmin && <><Button
          variant="outline"
          className="border-red-300 text-red-600 hover:bg-red-50"
          onClick={() => {
            const issued = (rows || []).filter((r) => r.coa_no);
            if (!issued.length) {
              toast.info("No COA certificates issued yet.");
              return;
            }
            printCoaRegister(issued);
          }}
          data-testid="coa-register-btn"
        >
          <Printer className="mr-2 h-4 w-4" /> COA Register
        </Button>
        <Button
          variant="outline"
          className="border-red-300 text-red-600 hover:bg-red-50"
          onClick={() => {
            const openClaims = (rows || []).filter((r) => r.coa_no && (r.coa_claim_status || "Not Filed") !== "Settled");
            if (!openClaims.length) {
              toast.info("No open claims — all COA claims are settled.");
              return;
            }
            printClaimAgeing(rows || []);
          }}
          data-testid="claim-ageing-btn"
        >
          <Printer className="mr-2 h-4 w-4" /> Claim Ageing
        </Button>
        <Button
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          onClick={shareAgeingWhatsApp}
          data-testid="ageing-whatsapp-btn"
        >
          <MessageCircle className="mr-2 h-4 w-4" /> Ageing → WA
        </Button></>}
        <Button
          variant="outline"
          className="border-red-300 text-red-600 hover:bg-red-50"
          onClick={() => {
            const under = underInsured().sort((a, b) => String(a.date).localeCompare(String(b.date)));
            if (!under.length) {
              toast.info("No under-insured consignments — every load is fully covered.");
              return;
            }
            const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
            const lines = [
              [`Under-Insured Consignments — as on ${fmtDate(today())}`],
              ["Sl No", "Month", "Date", "LR No.", "Party", "Route", "Invoice Value", "Insured Amount", "Shortfall"],
              ...under.map((r, i) => [i + 1, String(r.date || "").slice(0, 7), r.date ? fmtDate(r.date) : "", String(r.lr || "").replace(/^CN[-\s]?/i, ""), r.party, r.route, r.invTotal, r.insured, r.short]),
              ["", "", "", "", "", "TOTAL", under.reduce((s, r) => s + r.invTotal, 0), under.reduce((s, r) => s + r.insured, 0), under.reduce((s, r) => s + r.short, 0)],
            ];
            const blob = new Blob(["\ufeff" + lines.map((l) => l.map(cell).join(",")).join("\n")], { type: "text/csv;charset=utf-8;" });
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `under-insured-${today()}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success(`Excel downloaded — ${under.length} under-insured consignment${under.length > 1 ? "s" : ""}`);
          }}
          data-testid="insured-excel-btn"
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" /> Insured Excel
        </Button>
        <Button variant="outline" onClick={() => setBlankLrOpen(true)} data-testid="blank-lr-print-btn">
          <Printer className="mr-2 h-4 w-4" /> Blank LR Print
        </Button>
        <PadLogButton />
        <span className="text-xs text-muted-foreground">Leave dates blank to print the entire register.</span>
      </div>

      {(() => {
        const needle = q.trim().toLowerCase();
        const searchMatch = (r) => {
          if (!needle) return true;
          const hay = [String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, ""), r.consignor, r.consignee, rowCode(r), r.origin, r.destination, r.vehicle_no, r.invoice_no, r.dc_no, ...(r.extra_invoices || []).map((x) => x.no), ...(r.extra_dcs || []).map((x) => x.no)];
          return hay.some((x) => String(x || "").toLowerCase().includes(needle));
        };
        const list = (rows || []).filter(searchMatch);
        const byBranch = {};
        list.forEach((r) => {
          const b = r.office || r.office_name || "—";
          if (!byBranch[b]) byBranch[b] = { total: 0, delivered: 0, cancelled: 0 };
          byBranch[b].total++;
          const v = String(r.status || "").toLowerCase();
          if (v === "delivered") byBranch[b].delivered++;
          else if (v === "cancelled") byBranch[b].cancelled++;
        });
        const names = Object.keys(byBranch).sort();
        if (names.length < 2) return null;
        return (
          <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="branch-delivery-pills">
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">Branch delivery:</span>
            {names.map((b) => {
              const s = byBranch[b];
              const el = s.total - s.cancelled;
              const pct = el > 0 ? Math.round((s.delivered / el) * 100) : 0;
              const cls = pct >= 90 ? "bg-emerald-100 text-emerald-800" : pct >= 60 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700";
              const active = branchFilter === b;
              return (
                <button
                  type="button"
                  key={b}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-transform hover:scale-105 ${cls} ${active ? "ring-2 ring-slate-700 ring-offset-1" : ""}`}
                  title={active ? "Tap again to show all branches" : `Show only ${b} consignments`}
                  onClick={() => { setBranchFilter(active ? "All" : b); setPage(1); }}
                  data-testid={`branch-pill-${b.toLowerCase()}`}
                >
                  {b}: {pct}% ({s.delivered}/{el})
                </button>
              );
            })}
          </div>
        );
      })()}

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-consignments" aria-label="Select all" />
              </TableHead>
              {COLS.filter((h) => h !== "COA" || isAdmin).map((h) => (
                <TableHead key={h} className="whitespace-nowrap text-[11px] uppercase tracking-[0.1em] text-slate-500">
                  {h}
                </TableHead>
              ))}
              <TableHead className="text-right text-[11px] uppercase tracking-[0.1em] text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: COLS.length + 2 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-16" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && rows.filter(viewMatch).length === 0 && (
              <TableRow>
                <TableCell colSpan={COLS.length + 2} className="py-12 text-center text-muted-foreground">
                  No consignments{branchFilter !== "All" ? ` for ${branchFilter}` : " yet. Create your first consignment entry."}
                </TableCell>
              </TableRow>
            )}
            {rows &&
              rows.filter(viewMatch).slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((r, idx) => (
                <TableRow key={r.id} data-testid="consignment-row" className={`whitespace-nowrap ${highlightId === r.id ? "bg-amber-100 hover:bg-amber-100" : ""}`} data-state={selected.has(r.id) ? "selected" : undefined}>
                  <TableCell className="w-10">
                    <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} data-testid="select-consignment" aria-label="Select consignment" />
                  </TableCell>
                  <TableCell className="text-sm">{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                  <TableCell className="text-sm" data-testid="consignment-branch-cell"><BranchCell value={r.office || r.office_name} /></TableCell>
                  <TableCell className="text-sm font-medium">
                    {String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "—"}
                    <NewBadge createdAt={r.created_at} />
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(r.lr_date || r.dispatch_date)}</TableCell>
                  <TableCell className="text-sm">{r.origin || "—"}</TableCell>
                  <TableCell className="text-sm">{r.destination || "—"}</TableCell>
                  <TableCell className="text-sm">{r.consignor || "—"}</TableCell>
                  <TableCell className="text-sm">{r.consignee || "—"}</TableCell>
                  <TableCell className="text-sm">{customers.find((c) => c.id === r.customer_id)?.customer_code || "—"}</TableCell>
                  <TableCell className="text-sm">{r.basis || "—"}</TableCell>
                  <TableCell className="text-sm">{r.mode_of_operation || "—"}</TableCell>
                  <TableCell className="text-sm">{r.gst_payable_by || "—"}</TableCell>
                  <TableCell className="text-sm">{r.payable_by || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="ce-invoice-no-cell">
                    {r.invoice_no || "—"}
                    {(r.extra_invoices || []).length > 0 && <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] font-bold text-slate-600">+{r.extra_invoices.length}</span>}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(r.invoice_date)}</TableCell>
                  <TableCell className="text-sm tabular-nums">
                    {(() => {
                      const tot = (Number(r.invoice_value) || 0) + (r.extra_invoices || []).reduce((s, x) => s + (Number(x.value) || 0), 0);
                      if (!tot) return "—";
                      const ins = Number(r.insurance_amount) || 0;
                      return (
                        <>
                          {inr(tot)}
                          {tot > ins && (
                            <span className="ml-1 rounded bg-red-100 px-1 text-[10px] font-bold text-red-600" title={ins ? `Insured only ${inr(ins)} — short ${inr(tot - ins)}` : "No insured amount entered"} data-testid="insurance-shortfall-badge">
                              SHORT
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-sm" data-testid="dc-no-cell">
                    {r.dc_no || "—"}
                    {(r.extra_dcs || []).length > 0 && <span className="ml-1 rounded bg-slate-100 px-1 text-[10px] font-bold text-slate-600">+{r.extra_dcs.length}</span>}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDate(r.dc_date)}</TableCell>
                  <TableCell className="text-sm tabular-nums" data-testid="dc-value-cell">{Number(r.dc_value) ? inr(r.dc_value) : "—"}</TableCell>
                  <TableCell className="text-sm">{r.vehicle_type || "—"}</TableCell>
                  <TableCell className="text-sm">{r.weight_kgs || "—"}</TableCell>
                  <TableCell className="text-sm">{r.packages || "—"}</TableCell>
                  <TableCell className="text-sm">{r.said_to_contain || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.freight_amount)}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.lorry_hire)}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.advance_amount)}</TableCell>
                  <TableCell className="text-sm tabular-nums font-medium">{inr(r.balance_amount)}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.loading_charge)}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.unloading_charge)}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.halting_charge)}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.odc_charge)}</TableCell>
                  <TableCell className="text-sm tabular-nums">{inr(r.demurrage_charge)}</TableCell>
                  <TableCell className="text-sm tabular-nums" data-testid="fov-charge-cell">{inr(r.fov_charge)}</TableCell>
                  <TableCell className="text-sm">{r.eway_bill || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.eway_valid)}</TableCell>
                  <TableCell className="text-sm">{r.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="consignment-vehicle-type-cell">{r.vehicle_type || "—"}</TableCell>
                  <TableCell className="text-sm">{r.driver_mobile || "—"}</TableCell>
                  <TableCell className="text-sm">{r.vendor_name || "—"}</TableCell>
                  <TableCell className="text-sm">{r.vendor_code || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.delivery_date)}</TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  {isAdmin && <TableCell data-testid="coa-cell">
                    {r.coa_no ? (
                      <span className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          className="whitespace-nowrap rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700 hover:bg-red-100"
                          title={`Damage: ${r.coa_damage_desc || ""} — tap to print COA certificate`}
                          onClick={() => printCoaCertificate(withCoaPhotos(r))}
                          data-testid="coa-print-badge"
                        >
                          {r.coa_no}
                        </button>
                        <button
                          type="button"
                          className="rounded-full border border-green-300 bg-green-50 px-1.5 py-0.5 text-[11px] font-bold text-green-700 hover:bg-green-100"
                          title="Send COA certificate as image on WhatsApp"
                          onClick={() => shareCoaWhatsApp(withCoaPhotos(r))}
                          data-testid="coa-wa-btn"
                        >
                          WA
                        </button>
                        <button
                          type="button"
                          className="rounded-full border border-sky-300 bg-sky-50 p-0.5 text-sky-700 hover:bg-sky-100 disabled:opacity-50"
                          title="Email COA certificate PDF to your insurance agent"
                          disabled={emailingCoaId === r.id}
                          onClick={() => emailCoa(r)}
                          data-testid="coa-email-btn"
                        >
                          <Mail className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-0.5 rounded-full border border-amber-300 bg-amber-50 px-1 py-0.5 text-[11px] font-bold text-amber-700 hover:bg-amber-100"
                          title="Attach photos of the damaged goods"
                          onClick={() => setPhotoRow(r)}
                          data-testid="coa-photos-btn"
                        >
                          <Camera className="h-3.5 w-3.5" />{(r.coa_photos || []).length || ""}
                        </button>
                        <button
                          type="button"
                          className={`whitespace-nowrap rounded-full border px-1.5 py-0.5 text-[11px] font-bold ${
                            r.coa_claim_status === "Settled" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : r.coa_claim_status === "Approved" ? "border-violet-300 bg-violet-50 text-violet-700" : r.coa_claim_status === "Filed" ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-300 bg-white text-slate-500"
                          }`}
                          title={r.coa_claim_status === "Settled" && Number(r.coa_claim_amount) ? `Settled — ₹${Number(r.coa_claim_amount).toLocaleString("en-IN")} received` : "Track this COA's insurance claim: Filed / Approved / Settled"}
                          onClick={() => { setClaimRow(r); setClaimStatus(r.coa_claim_status || "Filed"); setClaimAmount(r.coa_claim_amount || ""); }}
                          data-testid="coa-claim-btn"
                        >
                          {r.coa_claim_status || "Claim?"}
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="whitespace-nowrap rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-500 hover:border-red-300 hover:text-red-600"
                        title="Material damaged in transit? Issue a COA certificate for the insurance claim"
                        onClick={() => { setCoaRow(r); setCoaDesc(""); setCoaValue(""); setCoaCategory(""); setCoaIssueValue(""); setCoaDate(new Date().toISOString().slice(0, 10)); }}
                        data-testid="coa-issue-btn"
                      >
                        + COA
                      </button>
                    )}
                  </TableCell>}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setPrintTarget(r)} data-testid="print-consignment-btn" title="Print consignment note">
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid="edit-consignment-btn" title={isAdmin ? "Edit consignment" : "Update consignment (Freight, Basis, Lorry Hire & Invoice details locked — Admin only)"}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!isAdmin && (
                        <>
                          {myCrs[r.id] && (
                            <span
                              className={`self-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-bold ${myCrs[r.id].status === "Approved" ? "border-emerald-300 bg-emerald-100 text-emerald-700" : myCrs[r.id].status === "Rejected" ? "border-red-300 bg-red-100 text-red-700" : "border-amber-300 bg-amber-100 text-amber-700"}`}
                              title={`${myCrs[r.id].field_label}: ${myCrs[r.id].old_value || "—"} → ${myCrs[r.id].new_value} — ${myCrs[r.id].status}${myCrs[r.id].decided_by ? ` by ${myCrs[r.id].decided_by}` : ""}`}
                              data-testid="cr-status-chip"
                            >
                              {myCrs[r.id].status === "Approved" ? "✔ Approved" : myCrs[r.id].status === "Rejected" ? "✖ Rejected" : "⏳ Pending"}
                            </span>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" onClick={() => { setCrRow(r); setCrField("freight_amount"); setCrValue(""); setCrReason(""); }} data-testid="request-change-btn" title="Request a freight/invoice correction (Admin approval)">
                            <History className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-600"
                        title="Share LR on WhatsApp"
                        data-testid="whatsapp-consignment-btn"
                        onClick={() => {
                          const cust = customers.find((c) => c.id === r.customer_id);
                          const digits = String(cust?.phone || "").replace(/\D/g, "");
                          const lines = [
                            `*CONSIGNMENT NOTE — Nisha Transport Corporation*`,
                            ``,
                            `LR No.: *${r.lr_no || r.consignment_no || "—"}*`,
                            `LR Date: ${r.lr_date ? fmtDate(r.lr_date) : "—"}`,
                            `From: ${r.origin || "—"}  →  To: ${r.destination || "—"}`,
                            `Consignor: ${r.consignor || "—"}`,
                            `Consignee: ${r.consignee || "—"}`,
                            r.vehicle_no ? `Vehicle No.: ${r.vehicle_no}` : "",
                            r.container_type ? `Container: ${r.container_type}` : "",
                            r.packages ? `Packages: ${r.packages}` : "",
                            r.said_to_contain ? `Goods: ${r.said_to_contain}` : "",
                            r.weight_tons ? `Weight: ${r.weight_tons} MT` : "",
                            r.freight_amount ? `Freight: ${inr(r.freight_amount)} (${r.basis || "—"})` : "",
                            r.eway_bill ? `E-Way Bill: ${r.eway_bill}` : "",
                            ``,
                            `— Nisha Transport Corporation`,
                          ].filter((l) => l !== "");
                          const msg = encodeURIComponent(lines.join("\n"));
                          window.open(digits ? `https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
                        }}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      {(() => {
                        const veh = findVehicle(r.vehicle_no);
                        if (!veh) return null;
                        return (
                          <>
                            {veh.gps_link && (
                              <a href={veh.gps_link} target="_blank" rel="noreferrer" title="Track GPS" data-testid="row-track-gps">
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary"><Satellite className="h-4 w-4" /></Button>
                              </a>
                            )}
                            {veh.fastag_link && (
                              <a href={veh.fastag_link} target="_blank" rel="noreferrer" title="Track FASTag" data-testid="row-track-fastag">
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-primary"><Radio className="h-4 w-4" /></Button>
                              </a>
                            )}
                          </>
                        );
                      })()}
                      {isAdmin && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        title="Delete consignment"
                        data-testid="delete-consignment-btn"
                        onClick={async () => {
                          const lr = String(r.lr_no || r.consignment_no || "").replace(/^CN[-\s]?/i, "") || "this consignment";
                          if (!window.confirm(`Delete LR ${lr}? This cannot be undone.`)) return;
                          try {
                            await api.delete(`/shipments/${r.id}`);
                            setRows((prev) => prev.filter((x) => x.id !== r.id));
                            setSelected((p) => { const n = new Set(p); n.delete(r.id); return n; });
                            toast.success(`LR ${lr} deleted`);
                          } catch (e) {
                            toast.error(formatApiError(e.response?.data?.detail));
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            {rows && rows.filter(viewMatch).length > 0 && (
              <TableRow className="bg-slate-100 font-bold" data-testid="consignment-total-row">
                <TableCell colSpan={19} className="text-right text-sm">TOTAL</TableCell>
                <TableCell className="text-sm tabular-nums text-primary" data-testid="dc-value-total">
                  {inr(rows.filter(viewMatch).reduce((s, r) => s + (Number(r.dc_value) || 0) + (r.extra_dcs || []).reduce((x, d) => x + (Number(d.value) || 0), 0), 0))}
                </TableCell>
                <TableCell colSpan={5} className="text-right text-sm">TOTAL FREIGHT</TableCell>
                <TableCell className="text-sm tabular-nums text-primary" data-testid="freight-total">
                  {inr(rows.filter(viewMatch).reduce((s, r) => s + (Number(r.freight_amount) || 0), 0))}
                </TableCell>
                <TableCell colSpan={COLS.length - 24}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableShell>

      <Dialog open={!!claimRow} onOpenChange={(o) => !o && setClaimRow(null)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="coa-claim-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Insurance Claim — {claimRow?.coa_no}</DialogTitle>
            <DialogDescription>Track where this COA's claim stands with the insurer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-1.5">
              <Label>Claim status</Label>
              <Select value={claimStatus} onValueChange={setClaimStatus}>
                <SelectTrigger data-testid="coa-claim-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Not Filed">Not Filed</SelectItem>
                  <SelectItem value="Filed">Filed</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Settled">Settled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {claimStatus === "Settled" && (
              <div className="grid gap-1.5">
                <Label>Amount received (₹)</Label>
                <Input type="number" value={claimAmount} onChange={(e) => setClaimAmount(e.target.value)} placeholder="0" data-testid="coa-claim-amount-input" />
              </div>
            )}
            <div className="grid gap-1.5 border-t border-border pt-3">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Follow-up Diary</Label>
              <div className="max-h-40 space-y-1.5 overflow-y-auto" data-testid="claim-notes-list">
                {(claimRow?.coa_notes || []).length ? (
                  [...(claimRow?.coa_notes || [])].reverse().map((n) => (
                    <div key={n.at} className="flex items-start gap-2 rounded-md border border-border bg-slate-50 px-2.5 py-1.5" data-testid="claim-note-row">
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] text-muted-foreground">{fmtDate(String(n.at || "").slice(0, 10))} · {n.by || ""}</div>
                        <div className="break-words text-sm text-foreground">{n.text}</div>
                      </div>
                      <button type="button" className="mt-0.5 text-slate-400 hover:text-red-600" onClick={() => deleteClaimNote(n.at)} data-testid="claim-note-delete">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No follow-up notes yet — jot each insurer call here.</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addClaimNote()}
                  placeholder="e.g. Spoke to surveyor, report expected Friday"
                  data-testid="claim-note-input"
                />
                <Button variant="outline" size="sm" className="h-9 shrink-0" disabled={noteBusy || !noteText.trim()} onClick={addClaimNote} data-testid="claim-note-add-btn">
                  {noteBusy ? "…" : "Add"}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClaimRow(null)}>Cancel</Button>
            <Button disabled={claimSaving} onClick={saveClaim} data-testid="coa-claim-save-btn">{claimSaving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!crRow} onOpenChange={(o) => !o && setCrRow(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="change-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Request Correction — LR {crRow ? String(crRow.lr_no || crRow.consignment_no || "").replace(/^CN[-\s]?/i, "") : ""}</DialogTitle>
            <DialogDescription>Locked fields need Admin approval. Your request will appear on the admin dashboard.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="grid gap-1.5">
              <Label>Field to correct</Label>
              <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={crField} onChange={(e) => setCrField(e.target.value)} data-testid="cr-field-select">
                <option value="freight_amount">Freight Amount</option>
                <option value="basis">Basis</option>
                <option value="lorry_hire">Lorry Hire</option>
                <option value="invoice_no">Invoice No.</option>
                <option value="invoice_date">Invoice Date</option>
                <option value="invoice_value">Invoice Value</option>
              </select>
              <p className="text-xs text-muted-foreground">Current value: <b>{crRow ? String(crRow[crField] ?? "") || "—" : ""}</b></p>
            </div>
            <div className="grid gap-1.5">
              <Label>New value</Label>
              <Input type={crField === "invoice_date" ? "date" : ["freight_amount", "lorry_hire", "invoice_value"].includes(crField) ? "number" : "text"} value={crValue} onChange={(e) => setCrValue(e.target.value)} data-testid="cr-value-input" />
            </div>
            <div className="grid gap-1.5">
              <Label>Reason</Label>
              <Input value={crReason} onChange={(e) => setCrReason(e.target.value)} placeholder="e.g. Rate agreed was ₹12,000, entered wrongly" data-testid="cr-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrRow(null)}>Cancel</Button>
            <Button disabled={crBusy || !String(crValue).trim()} onClick={submitChangeRequest} data-testid="cr-submit-btn">{crBusy ? "Sending…" : "Send Request"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!photoRow} onOpenChange={(o) => !o && setPhotoRow(null)}>
        <DialogContent className="sm:max-w-[480px]" data-testid="coa-photos-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Damage Photos — {photoRow?.coa_no}</DialogTitle>
            <DialogDescription>Attach up to 6 photos of the damaged goods. They print on the certificate and go with the insurer email.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {(photoRow?.coa_photos || []).map((p) => (
              <div key={p} className="relative" data-testid="coa-photo-thumb">
                <img src={`${API}/files/${p}?auth=${token}`} alt="damage" className="h-24 w-32 rounded-md border border-border object-cover" />
                <button
                  type="button"
                  className="absolute -right-1.5 -top-1.5 grid h-5 w-5 place-items-center rounded-full bg-red-600 text-white hover:bg-red-700"
                  onClick={() => deleteCoaPhoto(p)}
                  data-testid="coa-photo-delete"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {!(photoRow?.coa_photos || []).length && <p className="py-4 text-sm text-muted-foreground">No photos attached yet.</p>}
          </div>
          <label className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 hover:bg-amber-100 ${photoBusy ? "pointer-events-none opacity-50" : ""}`}>
            <Camera className="h-4 w-4" /> {photoBusy ? "Uploading…" : "Add photos (JPG/PNG)"}
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => { uploadCoaPhotos(e.target.files); e.target.value = ""; }} data-testid="coa-photo-input" />
          </label>
          <label className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 ${photoBusy ? "pointer-events-none opacity-50" : ""}`}>
            <Camera className="h-4 w-4" /> {photoBusy ? "Uploading…" : "Take photo with camera"}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { uploadCoaPhotos(e.target.files); e.target.value = ""; }} data-testid="coa-camera-input" />
          </label>
        </DialogContent>
      </Dialog>

      <Dialog open={!!coaRow} onOpenChange={(o) => !o && setCoaRow(null)}>
        <DialogContent className="sm:max-w-[440px]" data-testid="coa-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Issue COA Certificate — LR {String(coaRow?.lr_no || coaRow?.consignment_no || "").replace(/^CN[-\s]?/i, "")}</DialogTitle>
            <DialogDescription>Material damage in transit — this certificate is given to the party for the insurance claim.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-1.5">
              <Label>Nature & description of damage *</Label>
              <Textarea rows={3} value={coaDesc} onChange={(e) => setCoaDesc(e.target.value)} placeholder="e.g. 12 cartons crushed due to rain water seepage; goods partially damaged" data-testid="coa-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Damage category</Label>
                <Input list="coa-category-options" value={coaCategory} onChange={(e) => setCoaCategory(e.target.value)} placeholder="e.g. Wet Condition" data-testid="coa-category-input" />
                <datalist id="coa-category-options">
                  <option value="Wet Condition" />
                  <option value="Broken / Crushed" />
                  <option value="Torn Packing" />
                  <option value="Shortage" />
                  <option value="Leakage" />
                  <option value="Dent / Scratch" />
                </datalist>
              </div>
              <div className="grid gap-1.5">
                <Label>COA issue value (₹)</Label>
                <Input type="number" value={coaIssueValue} onChange={(e) => setCoaIssueValue(e.target.value)} placeholder="0" data-testid="coa-issue-value-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Estimated damage value (₹)</Label>
                <Input type="number" value={coaValue} onChange={(e) => setCoaValue(e.target.value)} placeholder="0" data-testid="coa-value-input" />
              </div>
              <div className="grid gap-1.5">
                <Label>Certificate date</Label>
                <Input type="date" value={coaDate} onChange={(e) => setCoaDate(e.target.value)} data-testid="coa-date-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoaRow(null)}>Cancel</Button>
            <Button variant="outline" className="border-green-300 text-green-700 hover:bg-green-50" disabled={coaSaving} onClick={() => issueCoa("wa")} data-testid="coa-save-wa-btn">
              {coaSaving ? "Issuing…" : "Issue & WhatsApp"}
            </Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" disabled={coaSaving} onClick={() => issueCoa("print")} data-testid="coa-save-btn">
              {coaSaving ? "Issuing…" : "Issue & Print"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {rows && rows.filter(viewMatch).length > PAGE_SIZE && (() => {
        const total = rows.filter(viewMatch).length;
        const pages = Math.ceil(total / PAGE_SIZE);
        const cur = Math.min(page, pages);
        return (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3" data-testid="consignment-pagination">
            <span className="text-xs text-muted-foreground">
              Showing {(cur - 1) * PAGE_SIZE + 1}–{Math.min(cur * PAGE_SIZE, total)} of {total} consignments
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8" disabled={cur === 1} onClick={() => setPage(cur - 1)} data-testid="consignment-prev-page">
                Prev
              </Button>
              {Array.from({ length: pages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === pages || Math.abs(p - cur) <= 2)
                .reduce((acc, p) => {
                  if (acc.length && p - acc[acc.length - 1] > 1) acc.push("…");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "…" ? (
                    <span key={`e${i}`} className="px-1 text-xs text-muted-foreground">…</span>
                  ) : (
                    <Button
                      key={p}
                      variant={p === cur ? "default" : "outline"}
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => setPage(p)}
                      data-testid={`consignment-page-${p}`}
                    >
                      {p}
                    </Button>
                  )
                )}
              <Button variant="outline" size="sm" className="h-8" disabled={cur === pages} onClick={() => setPage(cur + 1)} data-testid="consignment-next-page">
                Next
              </Button>
            </div>
          </div>
        );
      })()}

      <Dialog open={binOpen} onOpenChange={setBinOpen}>
        <DialogContent className="sm:max-w-[720px] max-h-[80vh] overflow-y-auto" data-testid="recycle-bin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center justify-between gap-4">
              <span>Recycle Bin</span>
              {binItems && binItems.length > 0 && (
                <Button variant="destructive" size="sm" onClick={emptyBin} data-testid="empty-recycle-bin-btn">
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Empty Bin
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          {binItems === null && <Skeleton className="h-24 w-full" />}
          {binItems && binItems.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Recycle bin is empty.</p>}
          {binItems && binItems.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Record", "Deleted By", "Deleted At", "Actions"].map((h) => (
                    <TableHead key={h} className={`text-[11px] uppercase tracking-[0.15em] text-slate-500 ${h === "Actions" ? "text-right" : ""}`}>{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {binItems.map((item) => (
                  <TableRow key={item.id} data-testid="recycle-bin-row">
                    <TableCell className="text-sm font-medium">{item.entity}: {item.ref}</TableCell>
                    <TableCell className="text-sm">{item.deleted_by || "—"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{item.deleted_at ? new Date(item.deleted_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-emerald-700" onClick={() => restoreItem(item)} data-testid="restore-recycled-btn">
                          <RotateCcw className="h-3.5 w-3.5" /> Restore
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs text-destructive hover:text-destructive" onClick={() => purgeItem(item)} data-testid="purge-recycled-btn">
                          <Trash2 className="h-3.5 w-3.5" /> Delete Permanently
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[80vh] overflow-y-auto" data-testid="delete-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Delete Log</DialogTitle>
          </DialogHeader>
          {logs === null && <Skeleton className="h-24 w-full" />}
          {logs && logs.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No deletions recorded yet.</p>}
          {logs && logs.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  {["Record", "Deleted By", "Branch", "Deleted At"].map((h) => (
                    <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l) => (
                  <TableRow key={l.id} data-testid="delete-log-row">
                    <TableCell className="text-sm font-medium">{l.entity}: {l.ref}</TableCell>
                    <TableCell className="text-sm">{l.deleted_by || l.deleted_by_email || "—"}</TableCell>
                    <TableCell className="text-sm">{l.office || "—"}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{l.deleted_at ? new Date(l.deleted_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {open && dlgView === "min" && (
        <div className="fixed bottom-4 right-4 z-[60] flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2.5 shadow-xl" data-testid="consignment-dialog-minbar">
          <FileText className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">{editing ? "Edit Consignment" : "New Consignment"}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" title="Restore" onClick={() => setDlgView("normal")} data-testid="consignment-dialog-restore">
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Close" onClick={() => { setOpen(false); setDlgView("normal"); }}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <Dialog open={open && dlgView !== "min"} onOpenChange={(v) => { setOpen(v); if (!v) setDlgView("normal"); }}>
        <DialogContent className={dlgView === "max" ? "h-[95vh] max-h-[95vh] w-[97vw] max-w-[97vw] overflow-y-auto" : "max-h-[92vh] overflow-y-auto sm:max-w-[760px]"}>
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="flex items-center gap-2 font-display">
                <FileText className="h-4 w-4 text-primary" /> {editing ? "Edit Consignment" : "New Consignment"}
              </DialogTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Minimize" onClick={() => setDlgView("min")} data-testid="consignment-dialog-minimize">
                  <Minus className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title={dlgView === "max" ? "Restore size" : "Maximize"} onClick={() => setDlgView(dlgView === "max" ? "normal" : "max")} data-testid="consignment-dialog-maximize">
                  {dlgView === "max" ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </DialogHeader>

          {!editing && (
            <div className="flex gap-2">
              <Button type="button" size="sm" variant={mode === "single" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("single")} data-testid="consignment-single-mode">
                Single Consignment
              </Button>
              <Button type="button" size="sm" variant={mode === "multi" ? "default" : "outline"} className="h-7 text-xs" onClick={() => setMode("multi")} data-testid="consignment-multi-mode">
                Multiple Consignment (One vehicle)
              </Button>
            </div>
          )}

          {mode === "multi" ? (
            <div className="space-y-5 py-2">
              <section>
                <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Shared Trip Details (apply to all rows)</h4>
                <div className="grid gap-4 sm:grid-cols-3">
                  <TextField k="lr_date" label="LR Date" type="date" form={form} onText={set} />
                  <TextField k="origin" label="From" placeholder="Chennai" form={form} onText={set} />
                  <TextField k="vehicle_no" label="Vehicle No." placeholder="TN 00 AA 0000" form={form} onText={set} />
                  <TextField k="vehicle_type" label="Vehicle Type" placeholder="e.g. 32 Feet" form={form} onText={set} />
                  <div className="grid gap-1.5">
                    <Label>Basis</Label>
                    <Select value={form.basis} onValueChange={(v) => set("basis", v)}>
                      <SelectTrigger data-testid="multi-basis"><SelectValue placeholder="Select basis" /></SelectTrigger>
                      <SelectContent>{BASIS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Payable By</Label>
                    <Select value={form.payable_by} onValueChange={(v) => set("payable_by", v)}>
                      <SelectTrigger data-testid="multi-payable"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{GST_PAYABLE.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </section>
              <section>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Consignments on this vehicle</h4>
                  <div className="flex items-center gap-2">
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={addMultiRow} data-testid="consignment-add-row"><Plus className="mr-1 h-3.5 w-3.5" /> Add row</Button>
                    <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={pasteMultiRows} data-testid="consignment-paste-rows">Paste from Excel</Button>
                  </div>
                </div>
                <div className="overflow-x-auto rounded-md border border-border">
                  <table className="w-full text-xs" data-testid="consignment-multi-table">
                    <thead className="bg-slate-50">
                      <tr className="[&>th]:whitespace-nowrap [&>th]:px-2 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold [&>th]:text-slate-500">
                        <th>Sl</th><th>LR No.</th><th>Consignor</th><th>Consignee</th><th>Destination</th><th>Branch</th><th>Packages</th><th>Said to Contain</th><th className="text-right">Freight</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {multiRows.length === 0 && (
                        <tr><td colSpan={10} className="px-2 py-6 text-center text-muted-foreground">No rows. Add a row or paste from Excel.</td></tr>
                      )}
                      {multiRows.map((r, i) => (
                        <tr key={i} className="border-t [&>td]:px-1.5 [&>td]:py-1" data-testid="consignment-multi-row">
                          <td className="text-center">{i + 1}</td>
                          <td><Input className="h-8 w-[90px] text-xs" value={r.lr_no} onChange={(e) => updateMultiRow(i, "lr_no", e.target.value)} data-testid={`crow-lr-${i}`} /></td>
                          <td><Input className="h-8 w-[130px] text-xs" value={r.consignor} onChange={(e) => updateMultiRow(i, "consignor", e.target.value)} /></td>
                          <td><Input className="h-8 w-[130px] text-xs" value={r.consignee} onChange={(e) => updateMultiRow(i, "consignee", e.target.value)} /></td>
                          <td><Input className="h-8 w-[110px] text-xs" value={r.destination} onChange={(e) => updateMultiRow(i, "destination", e.target.value)} /></td>
                          <td><Input className="h-8 w-[120px] text-xs" value={r.office_name} onChange={(e) => updateMultiRow(i, "office_name", e.target.value)} placeholder="If company veh." /></td>
                          <td><Input type="number" className="h-8 w-[70px] text-xs" value={r.packages} onChange={(e) => updateMultiRow(i, "packages", Number(e.target.value))} /></td>
                          <td><Input className="h-8 w-[140px] text-xs" value={r.said_to_contain} onChange={(e) => updateMultiRow(i, "said_to_contain", e.target.value)} /></td>
                          <td><Input type="number" className="h-8 w-[100px] text-xs text-right" value={r.freight_amount} onChange={(e) => updateMultiRow(i, "freight_amount", Number(e.target.value))} data-testid={`crow-freight-${i}`} /></td>
                          <td><Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeMultiRow(i)} title="Remove row"><span aria-hidden>✕</span></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1 text-right text-xs text-muted-foreground">{multiRows.length} row(s) · each row is saved as a separate consignment</div>
              </section>
            </div>
          ) : (
          <div className="space-y-6 py-2">
            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">LR Details</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField k="lr_no" label="LR No." placeholder="Leave blank to auto-generate" form={form} onText={set} />
                <TextField k="lr_date" label="LR Date" type="date" form={form} onText={set} />
                <div className="grid gap-1.5">
                  <Label>Basis</Label>
                  <Select value={form.basis} onValueChange={(v) => set("basis", v)} disabled={!!editing && !isAdmin}>
                    <SelectTrigger data-testid="field-basis" className={!!editing && !isAdmin ? "bg-slate-50" : ""}><SelectValue placeholder="Select basis" /></SelectTrigger>
                    <SelectContent>
                      {BASIS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {!!editing && !isAdmin ? <span className="text-[10px] font-semibold text-amber-600">One-time entry — Admin only</span> : null}
                </div>
                <div className="grid gap-1.5">
                  <Label>Mode of Operation</Label>
                  <Select value={form.mode_of_operation} onValueChange={(v) => set("mode_of_operation", v)}>
                    <SelectTrigger data-testid="field-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>
                      {MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>GST Payable By</Label>
                  <Select value={form.gst_payable_by} onValueChange={(v) => set("gst_payable_by", v)}>
                    <SelectTrigger data-testid="field-gst-payable"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {GST_PAYABLE.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-1.5">
                  <Label>Payable By</Label>
                  <Select value={form.payable_by} onValueChange={(v) => set("payable_by", v)}>
                    <SelectTrigger data-testid="field-payable-by"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {GST_PAYABLE.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(form.gst_payable_by === "THIRD PARTY" || form.payable_by === "THIRD PARTY") && (
                  <>
                    <TextField k="third_party_name" label="Third Party Name" placeholder="Third party name" form={form} onText={set} />
                    <TextField k="third_party_gst" label="Third Party GST Number" placeholder="e.g. 33ABCDE1234F1Z5" form={form} onText={set} />
                    <TextField k="third_party_address" label="Third Party Address" placeholder="Third party address" form={form} onText={set} />
                  </>
                )}
                <TextField k="origin" label="From" placeholder="Chennai" form={form} onText={set} />
                <TextField k="destination" label="Destination" placeholder="Bangalore" form={form} onText={set} />
                <div className="grid gap-1.5">
                  <Label>Branch</Label>
                  <Select value={form.office || ""} onValueChange={(v) => set("office", v)}>
                    <SelectTrigger data-testid="field-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                    <SelectContent>
                      {branchList.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Parties</h4>
              <div className="mb-3 flex items-center gap-2">
                <Label className="text-xs">Vehicle:</Label>
                <Button type="button" size="sm" variant={form.vehicle_ownership !== "Company" ? "default" : "outline"} className="h-7 text-xs" onClick={() => set("vehicle_ownership", "Hired")} data-testid="veh-hired">Hired / Broker</Button>
                <Button type="button" size="sm" variant={form.vehicle_ownership === "Company" ? "default" : "outline"} className="h-7 text-xs" onClick={() => set("vehicle_ownership", "Company")} data-testid="veh-company">Company Vehicle</Button>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField k="consignor" label="Consignor Name" placeholder="Sender" form={form} onText={set} listId="consignor-suggestions" />
                <TextField k="consignee" label="Consignee Name" placeholder="Receiver" form={form} onText={set} listId="consignee-suggestions" />
                <TextField k="consignor_address" label="Consignor Address" placeholder="Sender address" form={form} onText={set} />
                <TextField k="consignor_gst" label="Consignor GST No." placeholder="e.g. 33ABCDE1234F1Z5" form={form} onText={set} />
                <TextField k="consignee_address" label="Consignee Address" placeholder="Receiver address" form={form} onText={set} />
                <TextField k="consignee_gst" label="Consignee GST No." placeholder="e.g. 36ABCDE1234F1Z5" form={form} onText={set} />
                <div className="grid gap-1.5">
                  <Label>Customer Code</Label>
                  <Input
                    value={custCode}
                    placeholder="e.g. CUST-1001"
                    data-testid="field-customer-code"
                    onChange={(e) => {
                      const v = e.target.value;
                      setCustCode(v);
                      const key = v.trim().toLowerCase();
                      const c = key ? customers.find((x) => (x.customer_code || "").toLowerCase() === key) : null;
                      if (c) setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name }));
                    }}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label>Billing customer</Label>
                  <Select
                    value={form.customer_id}
                    onValueChange={(v) => {
                      if (v === "__third_party__") {
                        setForm((f) => ({ ...f, customer_id: v, customer_name: "THIRD PARTY" }));
                        setCustCode("");
                        return;
                      }
                      const c = customers.find((x) => x.id === v);
                      setForm((f) => ({ ...f, customer_id: v, customer_name: c ? c.name : "" }));
                      setCustCode(c?.customer_code || "");
                    }}
                  >
                    <SelectTrigger data-testid="field-customer"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__third_party__">Third Party</SelectItem>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.vehicle_ownership === "Company" ? (
                  <TextField k="office_name" label="Branch" placeholder="Branch" form={form} onText={set} />
                ) : (
                  <TextField k="vendor_name" label="Vendor/Broker Name" placeholder="Vehicle vendor / broker" form={form} onText={set} listId="vendor-name-list" />
                )}
                {form.vehicle_ownership !== "Company" && (
                  <TextField k="vendor_code" label="Vendor/Broker Code" placeholder="e.g. VB-101" form={form} onText={set} listId="vendor-code-list" />
                )}
                <datalist id="vendor-name-list">
                  {vendors.map((v) => <option key={v.id} value={v.name} />)}
                </datalist>
                <datalist id="vendor-code-list">
                  {vendors.filter((v) => v.vendor_code).map((v) => <option key={v.id} value={v.vendor_code}>{v.name}</option>)}
                </datalist>
                {form.vehicle_ownership !== "Company" && (
                  <TextField k="vendor_address" label="Vendor/Broker Address" placeholder="Vendor / broker address" form={form} onText={set} />
                )}
                {form.vehicle_ownership !== "Company" && (
                  <TextField k="vendor_gst_no" label="Vendor/Broker GST No." placeholder="e.g. 33ABCDE1234F1Z5" form={form} onText={set} />
                )}
                {form.vehicle_ownership !== "Company" &&
                  String(form.vendor_name || "").trim() &&
                  !vendors.some((x) => String(x.name || "").trim().toLowerCase() === String(form.vendor_name).trim().toLowerCase()) && (
                    <div className="sm:col-span-3">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await api.post("/vendors", {
                              name: String(form.vendor_name).trim(),
                              vendor_code: String(form.vendor_code || "").trim(),
                              address: String(form.vendor_address || "").trim(),
                              gstin: String(form.vendor_gst_no || "").trim(),
                              type: "Broker",
                            });
                            setVendors((v) => [...v, res.data]);
                            if (res.data?.vendor_code && !form.vendor_code) set("vendor_code", res.data.vendor_code);
                            toast.success(`${form.vendor_name} saved to Vendor Master${res.data?.vendor_code ? ` (code ${res.data.vendor_code})` : ""}`);
                          } catch (e) {
                            toast.error(formatApiError(e.response?.data?.detail));
                          }
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-blue-400 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                        data-testid="vendor-quick-add-btn"
                      >
                        <Plus className="h-3.5 w-3.5" /> Save "{form.vendor_name}" to Vendor Master
                      </button>
                    </div>
                  )}
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Vehicle</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField k="vehicle_no" label="Vehicle No." placeholder="TN 00 AA 0000" form={form} onText={set} />
                <TextField k="vehicle_type" label="Vehicle Type" placeholder="e.g. 17 Feet, Container" form={form} onText={set} />
                <TextField k="weight_kgs" label="Weight" placeholder="e.g. 8500" form={form} onText={set} />
                <NumberField k="packages" label="No. of Packages" form={form} onNum={setNum} />
                <TextField k="said_to_contain" label="Description (Said to Contain)" placeholder="Said to contain..." form={form} onText={set} />
                <TextField k="driver_mobile" label="Driver Mobile No" placeholder="10-digit mobile" form={form} onText={set} />
                <TextField k="owner_mobile" label="Vehicle Owner Mobile No" placeholder="10-digit mobile" form={form} onText={set} />
                <div className="grid gap-1.5">
                  <Label>Vehicle Status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Amounts (₹)</h4>
              {freightRateHint && (
                <p className={`mb-2 rounded-md border px-3 py-1.5 text-xs ${freightRateHint.tone === "expired" ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-800"}`} data-testid="freight-rate-hint">
                  {freightRateHint.text}
                </p>
              )}
              {lowRateWarning && (
                <p className="mb-2 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700" data-testid="entry-low-rate-warning">
                  {lowRateWarning}
                </p>
              )}
              <div className="grid gap-4 sm:grid-cols-3">
                <NumberField k="freight_amount" label="Freight Amount" form={form} onNum={setNum} disabled={!!editing && !isAdmin} hint="One-time entry — Admin only" />
                <NumberField k="lorry_hire" label="Lorry Hire" form={form} onNum={setNum} disabled={!!editing && !isAdmin} hint="One-time entry — Admin only" />
                <NumberField k="advance_amount" label="Advance Amount" form={form} onNum={setNum} />
                <div className="grid gap-1.5">
                  <Label>Balance Amount</Label>
                  <Input value={inr(balance)} readOnly disabled className="bg-slate-50 font-medium" data-testid="field-balance" />
                </div>
                <NumberField k="loading_charge" label="Loading Charge" form={form} onNum={setNum} />
                <NumberField k="unloading_charge" label="Unloading Charge" form={form} onNum={setNum} />
                <NumberField k="halting_charge" label="Halting charge" form={form} onNum={setNum} />
                <NumberField k="odc_charge" label="ODC Charge" form={form} onNum={setNum} />
                <NumberField k="demurrage_charge" label="Demurrage Charge" form={form} onNum={setNum} />
                <NumberField k="fov_charge" label="FOV Charge" form={form} onNum={setNum} />
              </div>
              {!!editing && !isAdmin && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                  <span className="text-xs text-amber-800">Freight, Basis &amp; Lorry Hire are one-time entries. Mistake at entry time?</span>
                  <Button type="button" variant="outline" size="sm" className="h-8 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => { setRc({ freight: form.freight_amount ?? "", basis: form.basis || "", hire: form.lorry_hire ?? "", reason: "" }); setRcOpen(true); }} data-testid="request-rate-change-btn">
                    Request Admin Change
                  </Button>
                </div>
              )}
              <Dialog open={rcOpen} onOpenChange={setRcOpen}>
                <DialogContent className="sm:max-w-[420px]" data-testid="rate-change-dialog">
                  <DialogHeader><DialogTitle className="font-display">Request Admin Change — LR {String(editing?.lr_no || editing?.consignment_no || "").replace(/^CN[-\s]?/i, "")}</DialogTitle></DialogHeader>
                  <div className="grid gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <Label>Correct Freight (₹)</Label>
                        <Input type="number" value={rc.freight} onChange={(e) => setRc((p) => ({ ...p, freight: e.target.value }))} data-testid="rc-freight" />
                      </div>
                      <div className="grid gap-1.5">
                        <Label>Correct Lorry Hire (₹)</Label>
                        <Input type="number" value={rc.hire} onChange={(e) => setRc((p) => ({ ...p, hire: e.target.value }))} data-testid="rc-hire" />
                      </div>
                    </div>
                    <div className="grid gap-1.5">
                      <Label>Correct Basis</Label>
                      <Select value={rc.basis} onValueChange={(v) => setRc((p) => ({ ...p, basis: v }))}>
                        <SelectTrigger data-testid="rc-basis"><SelectValue placeholder="Keep as is" /></SelectTrigger>
                        <SelectContent>{BASIS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-1.5">
                      <Label>Reason</Label>
                      <Textarea rows={2} value={rc.reason} onChange={(e) => setRc((p) => ({ ...p, reason: e.target.value }))} placeholder="e.g. Freight typed wrong at entry — agreed rate is ₹7,500" data-testid="rc-reason" />
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          await api.post("/rate-change-requests", {
                            shipment_id: editing.id,
                            lr_no: editing.lr_no || editing.consignment_no || "",
                            proposed_freight: rc.freight === "" ? null : Number(rc.freight),
                            proposed_basis: rc.basis || "",
                            proposed_lorry_hire: rc.hire === "" ? null : Number(rc.hire),
                            reason: rc.reason,
                          });
                          toast.success("Change request sent to Admin — it will appear on the admin dashboard");
                          setRcOpen(false);
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail));
                        }
                      }}
                      data-testid="rc-send-btn"
                    >
                      Send Request
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">Insurance</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField k="insurance_company" label="Insurance Company" placeholder="e.g. New India Assurance" form={form} onText={set} />
                <TextField k="insurance_policy_no" label="Policy No." placeholder="Policy number" form={form} onText={set} />
                <TextField k="insurance_date" label="Policy Date" type="date" form={form} onText={set} />
                <TextField k="insurance_expiry" label="Policy Expired" type="date" form={form} onText={set} />
                <NumberField k="insurance_amount" label="Insured Amount (₹)" form={form} onNum={setNum} />
                <TextField k="insurance_risk" label="Risk" placeholder="e.g. Transit risk" form={form} onText={set} />
              </div>
              {(() => {
                const invTotal = (Number(form.invoice_value) || 0) + (form.extra_invoices || []).reduce((s, x) => s + (Number(x.value) || 0), 0);
                const insured = Number(form.insurance_amount) || 0;
                if (!invTotal || invTotal <= insured) return null;
                return (
                  <div className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="insurance-shortfall-warning">
                    ⚠ Insurance shortfall: goods worth {inr(invTotal)} but {insured ? `insured only ${inr(insured)} — short by ${inr(invTotal - insured)}` : "no insured amount entered"}. Increase cover before the vehicle leaves.
                  </div>
                );
              })()}
            </section>

            <section>
              <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">E-Way Bill & Dates</h4>
              <div className="grid gap-4 sm:grid-cols-3">
                <TextField k="eway_bill" label="E Way Bill No" placeholder="Optional" form={form} onText={set} />
                <TextField k="eway_valid" label="E Way Bill Valid" type="date" form={form} onText={set} />
                <TextField k="delivery_date" label="Delivery Date" type="date" form={form} onText={set} />
                <TextField k="invoice_no" label="Invoice No." placeholder="Goods invoice no." form={form} onText={set} />
                <TextField k="invoice_date" label="Invoice Date" type="date" form={form} onText={set} />
                <NumberField k="invoice_value" label="Invoice Value (₹)" form={form} onNum={setNum} />
                <TextField k="dc_no" label="DC No." placeholder="Delivery challan no." form={form} onText={set} />
                <TextField k="dc_date" label="DC Date" type="date" form={form} onText={set} />
                <NumberField k="dc_value" label="DC Value (₹)" form={form} onNum={setNum} />
                <div className="space-y-3 rounded-md border border-dashed border-border p-3 sm:col-span-3" data-testid="multi-invoice-dc-section">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">
                    Multiple Invoice / DC Entry — one consignment can carry many, each with its own date
                  </div>
                  {(form.extra_invoices || []).map((ei, i) => (
                    <div key={`inv-${i}`} className="grid items-end gap-2 sm:grid-cols-6" data-testid="extra-invoice-row">
                      <div className="grid gap-1">
                        <Label className="text-xs">Invoice No. {i + 2}</Label>
                        <Input value={ei.no || ""} onChange={(e) => setExtra("extra_invoices", i, "no", e.target.value)} placeholder="Invoice no." data-testid="extra-invoice-no" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">Invoice Date</Label>
                        <Input type="date" value={ei.date || ""} onChange={(e) => setExtra("extra_invoices", i, "date", e.target.value)} data-testid="extra-invoice-date" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">Invoice Value (₹)</Label>
                        <Input type="number" value={ei.value || ""} onChange={(e) => setExtra("extra_invoices", i, "value", Number(e.target.value))} data-testid="extra-invoice-value" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">E-Way Bill No</Label>
                        <Input value={ei.eway || ""} onChange={(e) => setExtra("extra_invoices", i, "eway", e.target.value)} placeholder="Optional" data-testid="extra-invoice-eway" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">E-Way Valid</Label>
                        <Input type="date" value={ei.eway_valid || ""} onChange={(e) => setExtra("extra_invoices", i, "eway_valid", e.target.value)} data-testid="extra-invoice-eway-valid" />
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="h-9 w-fit text-red-500 hover:text-red-600" onClick={() => rmExtra("extra_invoices", i)} data-testid="remove-extra-invoice-btn">
                        Remove
                      </Button>
                    </div>
                  ))}
                  {(form.extra_dcs || []).map((ed, i) => (
                    <div key={`dc-${i}`} className="grid items-end gap-2 sm:grid-cols-4" data-testid="extra-dc-row">
                      <div className="grid gap-1">
                        <Label className="text-xs">DC No. {i + 2}</Label>
                        <Input value={ed.no || ""} onChange={(e) => setExtra("extra_dcs", i, "no", e.target.value)} placeholder="Delivery challan no." data-testid="extra-dc-no" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">DC Date</Label>
                        <Input type="date" value={ed.date || ""} onChange={(e) => setExtra("extra_dcs", i, "date", e.target.value)} data-testid="extra-dc-date" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">DC Value (₹)</Label>
                        <Input type="number" value={ed.value || ""} onChange={(e) => setExtra("extra_dcs", i, "value", Number(e.target.value))} data-testid="extra-dc-value" />
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="h-9 w-fit text-red-500 hover:text-red-600" onClick={() => rmExtra("extra_dcs", i)} data-testid="remove-extra-dc-btn">
                        Remove
                      </Button>
                    </div>
                  ))}
                  {(form.extra_eways || []).map((ew, i) => (
                    <div key={`ew-${i}`} className="grid items-end gap-2 sm:grid-cols-4" data-testid="extra-eway-row">
                      <div className="grid gap-1">
                        <Label className="text-xs">E-Way Bill No {i + 2}</Label>
                        <Input value={ew.no || ""} onChange={(e) => setExtra("extra_eways", i, "no", e.target.value)} placeholder="E-way bill no." data-testid="extra-eway-no" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">E-Way Valid Upto</Label>
                        <Input type="date" value={ew.valid || ""} onChange={(e) => setExtra("extra_eways", i, "valid", e.target.value)} data-testid="extra-eway-valid" />
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="h-9 w-fit text-red-500 hover:text-red-600" onClick={() => rmExtra("extra_eways", i)} data-testid="remove-extra-eway-btn">
                        Remove
                      </Button>
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => addExtra("extra_invoices")} data-testid="add-invoice-btn">
                      + Add Invoice
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => addExtra("extra_dcs")} data-testid="add-dc-btn">
                      + Add DC
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => addExtra("extra_eways")} data-testid="add-eway-btn">
                      + Add E-Way Bill
                    </Button>
                  </div>
                </div>
                {form.basis === "PAID" && (
                  <div className="grid gap-1.5">
                    <Label>Payment Mode (Paid booking)</Label>
                    <Select value={form.payment_mode || ""} onValueChange={(v) => set("payment_mode", v)}>
                      <SelectTrigger data-testid="field-payment-mode"><SelectValue placeholder="Select mode" /></SelectTrigger>
                      <SelectContent>
                        {["Cash", "Bank", "UPI", "RTGS", "NEFT", "IMPS", "DD", "Cheque"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.basis === "PAID" && (form.payment_mode === "DD" || form.payment_mode === "Cheque") && (
                  <>
                    <TextField k="cheque_no" label={`${form.payment_mode} No.`} placeholder="Cheque / DD number" form={form} onText={set} />
                    <TextField k="cheque_date" label={`${form.payment_mode} Date`} type="date" form={form} onText={set} />
                    <NumberField k="cheque_amount" label={`${form.payment_mode} Amount (₹)`} form={form} onNum={setNum} />
                    <TextField k="cheque_bank" label="Bank Name" placeholder="e.g. PNB" form={form} onText={set} />
                  </>
                )}
              </div>
              <div className="mt-4 grid gap-1.5">
                <Label>Remarks</Label>
                <Textarea value={form.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Any special instructions" />
              </div>
            </section>
          </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-consignment-btn">
              {saving ? "Saving…" : editing ? "Save Changes" : mode === "multi" ? `Create ${multiRows.filter((r) => r.lr_no || r.destination || r.consignor).length || ""} Consignments` : "Create Consignment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LrCopyChoiceDialog target={printTarget} onClose={() => setPrintTarget(null)} />
      <BlankLrDialog open={blankLrOpen} onClose={() => setBlankLrOpen(false)} onPrint={(list) => setPrintTarget(list)} existingRows={rows || []} />
      <datalist id="consignor-suggestions">
        {Array.from(new Set((rows || []).map((r) => (r.consignor || "").trim()).filter(Boolean))).sort().map((n) => <option key={n} value={n} />)}
      </datalist>
      <datalist id="consignee-suggestions">
        {Array.from(new Set((rows || []).map((r) => (r.consignee || "").trim()).filter(Boolean))).sort().map((n) => <option key={n} value={n} />)}
      </datalist>
    </div>
  );
}
